import { useCallback, useEffect, useMemo, useState } from "react";
import { feesApi, ApiError } from "@/api";
import type { Fee, FeesSummary, JobApplication } from "@/types";
import { Button, Spinner, Pill } from "@/components/ui";
import { feeTypeTone, feeStatusTone, humanise } from "@/lib/jobs";
import { formatMoney } from "@/lib/money";
import { FeeDrawer } from "./FeeDrawer";
import { CreateInvoiceDrawer } from "./CreateInvoiceDrawer";

const EMPTY_SUMMARY: FeesSummary = {
  count: 0,
  total_agreed: 0,
  total_invoiced: 0,
  total_paid: 0,
  total_remaining_to_invoice: 0,
  total_outstanding: 0,
};

/** Fees tab (Brief 04) — the per-job order book. Self-contained: it owns its own
 *  fetch/refresh against GET /fees?job_id and does NOT touch /detail (fees aren't
 *  in that payload). System-managed amounts/status are display-only. */
export function FeesTab({
  jobId,
  applications,
  hasClient,
  leadClientName,
  defaultReference,
  onNotice,
  onFeeCount,
  onInvoiced,
}: {
  jobId: number;
  applications: JobApplication[];
  /** Job has a client to bill — gates the New-invoice button. */
  hasClient: boolean;
  leadClientName: string | null;
  defaultReference: string;
  onNotice: (kind: "info" | "warn", message: string) => void;
  /** Reports the loaded fee count up so the tab label stays in sync. */
  onFeeCount?: (count: number) => void;
  /** Called after an invoice is raised here — JobDetail switches to the
   *  Invoices tab so the new invoice + correct count show. */
  onInvoiced?: () => void;
}) {
  const [fees, setFees] = useState<Fee[]>([]);
  const [summary, setSummary] = useState<FeesSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [drawer, setDrawer] = useState<Fee | "new" | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);

  const appRefById = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of applications) map.set(a.id, a.app_reference || `Application #${a.id}`);
    return map;
  }, [applications]);

  const load = useCallback(
    (opts: { initial?: boolean } = {}) => {
      let cancelled = false;
      if (opts.initial) setLoading(true);
      feesApi
        .listFees(jobId)
        .then((res) => {
          if (cancelled) return;
          setFees(res.items ?? []);
          setSummary(res.summary ?? EMPTY_SUMMARY);
          onFeeCount?.(res.summary?.count ?? 0);
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load fees.");
        })
        .finally(() => {
          if (!cancelled && opts.initial) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    },
    [jobId, onFeeCount],
  );

  useEffect(() => load({ initial: true }), [load]);

  if (loading) {
    return <div className="jobs-list__loading"><Spinner label="Loading fees…" /></div>;
  }

  return (
    <div className="job-fees">
      <div className="job-fees__head">
        <p className="job-drawer__muted">{summary.count} fee{summary.count === 1 ? "" : "s"}</p>
        <div className="job-fees__head-actions">
          <Button variant="primary" size="sm" onClick={() => setDrawer("new")}>New fee</Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!hasClient}
            title={hasClient ? undefined : "Add a client before invoicing"}
            onClick={() => setShowInvoice(true)}
          >
            New invoice
          </Button>
        </div>
      </div>

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <dl className="job-fees__summary">
        <SummaryStat label="Agreed" value={summary.total_agreed} />
        <SummaryStat label="Invoiced" value={summary.total_invoiced} />
        <SummaryStat label="Paid" value={summary.total_paid} />
        <SummaryStat label="Remaining to invoice" value={summary.total_remaining_to_invoice} />
        <SummaryStat label="Outstanding" value={summary.total_outstanding} />
      </dl>

      {fees.length === 0 ? (
        <p className="jobs-list__empty">No fees on this job yet. Add one to build the order book.</p>
      ) : (
        <ul className="job-fee-list">
          {fees.map((fee) => {
            const linkedRef = fee.application_id > 0 ? appRefById.get(fee.application_id) : null;
            return (
              <li key={fee.id} className="job-fee" onClick={() => setDrawer(fee)} title="Edit fee">
                <div className="job-fee__main">
                  <div className="job-fee__title-row">
                    <span className="job-fee__desc">{fee.description}</span>
                    <Pill tone={feeTypeTone(fee.fee_type)}>{humanise(fee.fee_type)}</Pill>
                    <Pill tone={feeStatusTone(fee.status)}>{humanise(fee.status)}</Pill>
                  </div>
                  <div className="job-fee__meta">
                    <span className="job-fee__amount numeric">{formatMoney(fee.amount)}</span>
                    <span className="job-drawer__muted">
                      {formatMoney(fee.amount_invoiced)} invoiced · {formatMoney(fee.amount_paid)} paid
                    </span>
                    {linkedRef ? <span className="job-task__link">↪ {linkedRef}</span> : null}
                  </div>
                  {fee.notes ? <p className="job-fee__notes">{fee.notes}</p> : null}
                </div>
                <span className="job-fee__chevron" aria-hidden="true">›</span>
              </li>
            );
          })}
        </ul>
      )}

      {drawer ? (
        <FeeDrawer
          jobId={jobId}
          fee={drawer === "new" ? null : drawer}
          applications={applications}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            onNotice("info", drawer === "new" ? "Fee added." : "Fee updated.");
            load();
          }}
          onDeleted={() => {
            setDrawer(null);
            onNotice("info", "Fee deleted.");
            load();
          }}
        />
      ) : null}

      {showInvoice ? (
        <CreateInvoiceDrawer
          jobId={jobId}
          leadClientName={leadClientName}
          defaultReference={defaultReference}
          onClose={() => setShowInvoice(false)}
          onSaved={(res) => {
            setShowInvoice(false);
            onNotice("info", `Invoice ${res.xero_invoice_number} raised — awaiting payment.`);
            // Switch to the Invoices tab (it refetches, so the new invoice +
            // correct count show). FeesTab refetches its own fees on remount.
            onInvoiced?.();
          }}
        />
      ) : null}
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="job-fees__stat">
      <dt>{label}</dt>
      <dd className="numeric">{formatMoney(value)}</dd>
    </div>
  );
}
